import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { appendCriteriaContains, appendCriteriaEquals, appendCriteriaIn, appendPageable, readPagedRows, } from '../utils/spring-api';
import { mapKaseDtoList, mapKaseDtoToTicketDetail } from '@/shared/services/kase.mapper';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const kasesUrl = API_ROUTES.kases;
const assignmentsUrl = API_ROUTES.caseAssignments;
const initialState = {
    loading: false,
    errorMessage: null,
    cases: [],
    totalItems: 0,
    reference: null,
    referenceLoading: false,
    selectedCase: null,
    detailLoading: false,
    saving: false,
    assignments: [],
    reopenAssignments: [],
    reopening: false,
};
function kaseReportQuery(params) {
    const q = new URLSearchParams();
    appendPageable(q, params.page, params.size, params.sort);
    appendCriteriaContains(q, 'phoneNumber', params.phoneNumber);
    appendCriteriaContains(q, 'caseNumber', params.caseNumber);
    appendCriteriaContains(q, 'remark', params.remark);
    appendCriteriaContains(q, 'subject', params.subject);
    appendCriteriaEquals(q, 'provinceId', params.provinceId);
    appendCriteriaEquals(q, 'districtId', params.districtId);
    appendCriteriaIn(q, 'issueId', params.issueIds);
    appendCriteriaContains(q, 'branchName', params.branchName);
    appendCriteriaEquals(q, 'startDate', params.startDate);
    appendCriteriaEquals(q, 'endDate', params.endDate);
    appendCriteriaEquals(q, 'status', params.status);
    appendCriteriaEquals(q, 'userUsername', params.userUsername);
    appendCriteriaEquals(q, 'assignedAgentUsername', params.assignedAgentUsername);
    appendCriteriaEquals(q, 'isIncludeAssignmentCase', params.isIncludeAssignmentCase ? true : undefined);
    return q.toString();
}
export const getCaseReportReference = createAsyncThunk('caseReport/reference', async () => {
    const [issuesRes, usersRes] = await Promise.all([
        axios.get(`${API_ROUTES.issues}?page=0&size=1000`),
        axios.get(`${API_ROUTES.users}?page=0&size=1000&sort=id,desc`),
    ]);
    const rawIssues = Array.isArray(issuesRes.data) ? issuesRes.data : issuesRes.data?.content ?? [];
    const rawUsers = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.content ?? [];
    return {
        data: {
            issues: rawIssues.map((issue) => ({
                id: String(issue.id ?? ''),
                name: issue.name ?? '',
            })),
            users: rawUsers
                .map((user) => {
                const username = user.username?.trim() || user.login?.trim() || '';
                if (!username)
                    return null;
                const name = user.name?.trim() || user.fullName?.trim() || username;
                return { username, name };
            })
                .filter((user) => user != null),
            provinces: [],
            districts: [],
            branches: [],
            statuses: [],
        },
    };
}, { serializeError: serializeAxiosError });
export async function fetchCaseReportRows(params, options) {
    const res = await axios.get(`${kasesUrl}?${kaseReportQuery(params)}`, { signal: options?.signal });
    const { rows, total } = readPagedRows(res.data, res.headers);
    return { rows: mapKaseDtoList(rows), total };
}
export const getCaseReportCases = createAsyncThunk('caseReport/cases', async (params, { signal }) => {
    const { rows, total } = await fetchCaseReportRows(params, { signal });
    return { data: rows, headers: { 'x-total-count': String(total) } };
}, { serializeError: serializeAxiosError });
export const getCaseReportDetail = createAsyncThunk('caseReport/detail', async (caseId) => {
    const [kaseRes, assignmentsRes] = await Promise.all([
        axios.get(`${kasesUrl}/${caseId}`),
        axios.get(`${assignmentsUrl}?kaseId.equals=${encodeURIComponent(caseId)}&page=0&size=1000`),
    ]);
    const assignmentData = assignmentsRes.data;
    const assignments = Array.isArray(assignmentData)
        ? assignmentData
        : assignmentData?.content ?? [];
    return {
        data: {
            ...mapKaseDtoToTicketDetail(kaseRes.data),
            assignments,
        },
    };
}, { serializeError: serializeAxiosError });
export const getCaseReportAssignmentsForReopen = createAsyncThunk('caseReport/assignments_for_reopen', async (caseId) => {
    const res = await axios.get(`${assignmentsUrl}?kaseId.equals=${encodeURIComponent(caseId)}&page=0&size=1000`);
    const assignmentData = res.data;
    const rows = Array.isArray(assignmentData) ? assignmentData : assignmentData?.content ?? [];
    return { data: rows };
}, { serializeError: serializeAxiosError });
export const reopenCaseReportAssignments = createAsyncThunk('caseReport/reopen_assignments', async (payload) => {
    const res = await axios.post(API_ROUTES.kaseReopenAssignments(payload.caseId), {
        assignmentIds: payload.assignmentIds.map((id) => Number(id)),
        reason: payload.reason.trim(),
    });
    return res.data;
}, { serializeError: serializeAxiosError });
function buildCaseReportUpdateBody(payload) {
    const body = {
        id: Number(payload.caseId),
    };
    if (payload.remark !== undefined)
        body.remark = payload.remark;
    if (payload.subject !== undefined)
        body.subject = payload.subject;
    if (payload.issueId)
        body.issueId = Number(payload.issueId);
    if (payload.provinceId)
        body.provinceId = Number(payload.provinceId);
    if (payload.districtId)
        body.districtId = Number(payload.districtId);
    if (payload.branchName !== undefined)
        body.branchName = payload.branchName;
    return body;
}
export const updateCaseReport = createAsyncThunk('caseReport/update', async (payload) => axios.patch(`${kasesUrl}/${payload.caseId}`, buildCaseReportUpdateBody(payload)), { serializeError: serializeAxiosError });
export const caseReportSlice = createSlice({
    name: 'caseReport',
    initialState: initialState,
    reducers: {
        clearCaseReportDetail(state) {
            state.selectedCase = null;
            state.assignments = [];
            state.reopenAssignments = [];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getCaseReportReference.fulfilled, (state, action) => {
            state.referenceLoading = false;
            state.reference = action.payload.data;
        })
            .addCase(getCaseReportCases.fulfilled, (state, action) => {
            state.loading = false;
            state.cases = action.payload.data;
            state.totalItems = parseInt(action.payload.headers['x-total-count'] || '0', 10);
        })
            .addCase(getCaseReportDetail.fulfilled, (state, action) => {
            state.detailLoading = false;
            state.selectedCase = action.payload.data;
            state.assignments = action.payload.data.assignments ?? [];
        })
            .addCase(updateCaseReport.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(getCaseReportAssignmentsForReopen.fulfilled, (state, action) => {
            state.reopenAssignments = action.payload.data;
        })
            .addCase(reopenCaseReportAssignments.fulfilled, (state) => {
            state.reopening = false;
        })
            .addMatcher(isPending(getCaseReportAssignmentsForReopen), (state) => {
            state.errorMessage = null;
        })
            .addMatcher(isPending(reopenCaseReportAssignments), (state) => {
            state.reopening = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getCaseReportAssignmentsForReopen, reopenCaseReportAssignments), (state, action) => {
            state.reopening = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isPending(getCaseReportReference), (state) => {
            state.referenceLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseReportCases), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseReportDetail), (state) => {
            state.detailLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(updateCaseReport), (state) => {
            state.saving = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(getCaseReportReference, getCaseReportCases, getCaseReportDetail, updateCaseReport), (state, action) => {
            state.loading = false;
            state.referenceLoading = false;
            state.detailLoading = false;
            state.saving = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { clearCaseReportDetail } = caseReportSlice.actions;
export default caseReportSlice.reducer;
